import { Badge } from "@/components/shadcn/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { cn } from "@/lib/utils";

type IngestedBadgeVariant = "success" | "warning" | "destructive";

const ingestedBadgeVariant = (pct: number): IngestedBadgeVariant => {
  if (pct >= 95) return "success";
  if (pct >= 80) return "warning";
  return "destructive";
};

const ingestedDotColor = (pct: number): string => {
  if (pct >= 95) return "bg-emerald-600";
  if (pct >= 80) return "bg-amber-500";
  return "bg-red-500";
};

interface IngestedBadgeProps {
  ingested: number;
  total: number;
  isCompact?: boolean;
  variant?: "badge" | "dot";
}

export const IngestedBadge = ({
  ingested,
  total,
  isCompact = true,
  variant = "badge",
}: IngestedBadgeProps) => {
  if (total === 0) return null;
  const pct = Math.round((ingested / total) * 100);

  if (variant === "dot") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={`${pct}% ingested`}
            className={cn(
              "inline-block size-1.5 rounded-full cursor-default shrink-0",
              ingestedDotColor(pct)
            )}
          />
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          <div className="space-y-0.5">
            <p className="font-medium">{pct}% ingested</p>
            <p className="opacity-80 tabular-nums">
              {ingested.toLocaleString()} of {total.toLocaleString()} documents
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Badge
      variant={ingestedBadgeVariant(pct)}
      isCompact={isCompact}
      title={`${ingested.toLocaleString()} / ${total.toLocaleString()} documents ingested`}
      className="tabular-nums"
    >
      {pct}%
    </Badge>
  );
};
